import { InputRule, wrappingInputRule } from "@handlewithcare/prosemirror-inputrules";
import { type NodeType } from "prosemirror-model";
import { Selection } from "prosemirror-state";

import { schema } from "@moment/prosemirror-schema";

/**
 * Given a list node type, returns input rules that:
 * 1. Turn a bullet (dash, plus, or asterisk) at the start of a textblock into a list item.
 * 2. Convert a to_do_item into a list_item when typing a bullet inside it.
 *
 * @param nodeType - The node type to wrap.
 * @returns - Input rules for list item creation and conversion.
 */
export const listItemRules = (nodeType: NodeType): InputRule[] => {
	const exp = /^\s*([-+*]|\d+\.)\s$/;

	return [
		wrappingInputRule(exp, nodeType, (match) => {
			return {
				// If match is a number followed by a dot, set it to #
				bullet: match[1]?.match(/^\d+\.$/) ? "#" : match[1],
				indent: 0,
			};
		}),
		// Convert to_do_item or list_item (different bullet) to list_item
		new InputRule(exp, (state, match, start, end) => {
			const $start = state.doc.resolve(start);
			const grandParent = $start.node(-1);
			const newBullet = match[1]?.match(/^\d+\.$/) ? "#" : match[1];

			// Convert from to_do_item
			if (grandParent.type === schema.nodes.to_do_item) {
				const tr = state.tr;
				tr.delete(start, end);
				const $mappedStart = tr.doc.resolve(start);
				tr.replaceWith(
					$mappedStart.before(-1),
					$mappedStart.after(-1),
					nodeType.create(
						{
							bullet: newBullet,
							indent: grandParent.attrs["indent"],
						},
						$mappedStart.node(-1).content
					)
				);
				tr.setSelection(Selection.findFrom(tr.doc.resolve(start), 1, true));
				return tr;
			}

			// Convert from list_item with different bullet type
			if (
				grandParent.type === schema.nodes.list_item &&
				grandParent.attrs["bullet"] !== newBullet
			) {
				const tr = state.tr;
				tr.delete(start, end);
				const $mappedStart = tr.doc.resolve(start);
				tr.replaceWith(
					$mappedStart.before(-1),
					$mappedStart.after(-1),
					nodeType.create(
						{
							bullet: newBullet,
							indent: grandParent.attrs["indent"],
						},
						$mappedStart.node(-1).content
					)
				);
				tr.setSelection(Selection.findFrom(tr.doc.resolve(start), 1, true));
				return tr;
			}

			return null;
		}),
	];
};
